import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Download, Plus, RefreshCw, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import EstadoBadge from '@/components/dispositivos/EstadoBadge';
import FormularioDispositivo from '@/components/dispositivos/FormularioDispositivo';
import { ChipBateria, ChipConexion } from '@/components/dispositivos/Chips';
import { useCatalogosDispositivos } from '@/components/dispositivos/catalogos';
import {
  ESTADO_LABEL,
  estadoGarantia,
  fechaCorta,
  MARCA_LABEL,
  SIN_MODELO,
  textoDonde,
  TIPO_LABEL,
  TIPOS,
  type Estado,
  type FilaDispositivo,
  type ListadoDispositivos,
  type Marca,
  type OpcionPropiedad,
  type ResultadoSincronizacion,
} from '@/lib/dispositivos';

/**
 * El inventario: qué aparatos hay, de qué tipo, en qué estado y dónde.
 *
 * Los filtros viven en la URL para que se pueda compartir un enlace («las
 * smartlocks en cuarentena»). «Sincronizar» lee las cuentas de Nuki y
 * SwitchBot y la copia de Hostify: crea lo que falte y refresca lo que ya
 * está, sin mover de casa nada que ya tenga ficha.
 */
export default function DispositivosInventarioPage() {
  const navigate = useNavigate();
  const { catalogos } = useCatalogosDispositivos();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') || '';
  const marca = params.get('marca') || '';
  const tipo = params.get('tipo') || '';
  const estado = params.get('estado') || '';
  const modelo = params.get('modelo') || '';
  const listingId = params.get('listingId') || '';
  const incluirBajas = params.get('incluirBajas') === 'true';

  const [texto, setTexto] = useState(q);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<ResultadoSincronizacion | null>(null);
  const [propiedades, setPropiedades] = useState<OpcionPropiedad[]>([]);

  const ponerFiltro = (clave: string, valor: string) => {
    const siguiente = new URLSearchParams(params);
    if (valor) siguiente.set(clave, valor);
    else siguiente.delete(clave);
    setParams(siguiente, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => { if (texto !== q) ponerFiltro('q', texto); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  useEffect(() => {
    api.getDispositivosPropiedades().then(setPropiedades).catch(() => setPropiedades([]));
  }, []);

  const { data, loading, error, refresh } = useApi<ListadoDispositivos>(
    () => api.getDispositivos({ q, marca, tipo, estado, modelo, listingId, incluirBajas }),
    [q, marca, tipo, estado, modelo, listingId, incluirBajas],
  );

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.sincronizarDispositivos();
      setResultadoSync(r);
      refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido sincronizar');
    } finally {
      setSincronizando(false);
    }
  };

  const crear = async (datos: Record<string, unknown>) => {
    const { dispositivo, dispositivos } = await api.crearDispositivo(datos);
    setMostrarAlta(false);
    refresh();
    // Un alta por cantidad no tiene «la» ficha: se vuelve a la lista de ese tipo.
    if (dispositivos && dispositivos.length > 1) navigate(`/dispositivos?tipo=${dispositivo.tipo}`);
    else navigate(`/dispositivos/${dispositivo.id}`);
  };

  const filas = data?.dispositivos || [];
  const hayFiltros = !!(q || marca || tipo || estado || modelo || listingId);

  // Los modelos que de verdad hay, no los 18 del catálogo: un desplegable con
  // opciones que no devuelven nada es peor que uno corto. Los cuenta el
  // servidor antes de aplicar este filtro (ver la ruta), así que elegir uno no
  // vacía la lista de opciones.
  const modelosEnUso = useMemo(
    () =>
      (data?.modelosEnLista ?? [])
        .map((m) => ({ ...m, label: catalogos.modeloLabel[m.id] || m.id }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [data?.modelosEnLista, catalogos.modeloLabel],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-sereno-dark/50">
          {data ? (
            <>
              <span className="font-medium text-sereno-dark">{filas.length}</span> en la lista ·{' '}
              {data.instalados} instalados · {data.enStock} en stock · {data.cuarentena} en cuarentena o reparación
              {data.sinModelo > 0 && (
                <>
                  {' · '}
                  <button
                    onClick={() => ponerFiltro('modelo', modelo === SIN_MODELO ? '' : SIN_MODELO)}
                    className="text-amber-700 underline underline-offset-2 hover:text-amber-900"
                    title={modelo === SIN_MODELO ? 'Quitar el filtro' : 'Ver solo los que no tienen modelo'}
                  >
                    {data.sinModelo} sin modelo
                  </button>
                </>
              )}
            </>
          ) : (
            ' '
          )}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => void sincronizar()} disabled={sincronizando} className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-50" title="Lee las cuentas de Nuki y SwitchBot y los custom fields de Hostify">
            <RefreshCw size={16} className={sincronizando ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{sincronizando ? 'Sincronizando…' : 'Sincronizar'}</span>
          </button>
          <a href={`/api/dispositivos/export${incluirBajas ? '?incluirBajas=true' : ''}`} className="btn-secondary flex items-center gap-1.5 text-sm" title="Descargar en Excel">
            <Download size={16} />
            <span className="hidden sm:inline">Exportar</span>
          </a>
          <button onClick={() => setMostrarAlta(true)} className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus size={16} />
            <span className="hidden sm:inline">Nuevo dispositivo</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {resultadoSync && (
        <div className="card mb-6 border border-sereno-blue bg-sereno-blue/20">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium">
                Sincronizado: {resultadoSync.contadores.creados} nuevos, {resultadoSync.contadores.actualizados} refrescados,{' '}
                {resultadoSync.contadores.instalacionesDeducidas} instalaciones deducidas de Hostify, {resultadoSync.contadores.enStock} en stock
                {resultadoSync.contadores.bridgesDeducidos ? `, ${resultadoSync.contadores.bridgesDeducidos} bridges deducidos en las casas que los necesitan` : ''}.
              </p>
              <p className="text-xs text-sereno-dark/60 mt-0.5">
                {resultadoSync.contadores.listingsActivos} listings activos, {resultadoSync.contadores.listingsConOpener} con opener en el custom field 2503
                {resultadoSync.contadores.sinModelo > 0 ? ` · ${resultadoSync.contadores.sinModelo} sin modelo` : ''}
                {resultadoSync.contadores.customFieldsRotos > 0 ? ` · ${resultadoSync.contadores.customFieldsRotos} custom fields rotos` : ''}
                {resultadoSync.contadores.yaNoEnCuenta > 0 ? ` · ${resultadoSync.contadores.yaNoEnCuenta} ya no en la cuenta` : ''}.
              </p>
              {(resultadoSync.limpieza.accesoriosBorrados > 0 || resultadoSync.limpieza.fichasActualizadas > 0) && (
                <p className="text-xs text-sereno-dark/60 mt-0.5">
                  Limpieza: {resultadoSync.limpieza.accesoriosBorrados} accesorio{resultadoSync.limpieza.accesoriosBorrados === 1 ? '' : 's'} (keypad o sensor)
                  que su aparato ya no confirma{resultadoSync.limpieza.fichasActualizadas > 0 ? `, ${resultadoSync.limpieza.fichasActualizadas} fichas puestas al día` : ''}.
                </p>
              )}
              {resultadoSync.avisos.length > 0 && (
                <ul className="mt-2 text-xs text-amber-800 list-disc list-inside space-y-0.5 max-h-40 overflow-y-auto">
                  {resultadoSync.avisos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
            <button onClick={() => setResultadoSync(null)} className="text-xs text-sereno-dark/50 hover:text-sereno-dark">Cerrar</button>
          </div>
        </div>
      )}

      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input type="text" placeholder="ID, alias, serie, casa…" className="input pl-9" value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
          <select className="input w-auto" value={marca} onChange={(e) => ponerFiltro('marca', e.target.value)}>
            <option value="">Todas las marcas</option>
            {(Object.keys(MARCA_LABEL) as Marca[]).map((m) => (
              <option key={m} value={m}>{MARCA_LABEL[m]}</option>
            ))}
          </select>
          <select className="input w-auto" value={tipo} onChange={(e) => ponerFiltro('tipo', e.target.value)}>
            <option value="">Todos los tipos</option>
            {TIPOS.map((t) => (
              <option key={t} value={t}>{TIPO_LABEL[t]}</option>
            ))}
          </select>
          <select className="input w-auto max-w-[200px]" value={modelo} onChange={(e) => ponerFiltro('modelo', e.target.value)}>
            <option value="">Todos los modelos</option>
            {modelosEnUso.map((m) => (
              <option key={m.id} value={m.id}>{m.label} ({m.n})</option>
            ))}
            {(data?.sinModelo ?? 0) > 0 || modelo === SIN_MODELO ? <option value={SIN_MODELO}>Sin modelo</option> : null}
          </select>
          <select className="input w-auto" value={estado} onChange={(e) => ponerFiltro('estado', e.target.value)}>
            <option value="">Todos los estados</option>
            {(Object.keys(ESTADO_LABEL) as Estado[]).map((e) => (
              <option key={e} value={e}>{ESTADO_LABEL[e]}</option>
            ))}
          </select>
          <select className="input w-auto max-w-[220px]" value={listingId} onChange={(e) => ponerFiltro('listingId', e.target.value)}>
            <option value="">Todas las casas</option>
            {propiedades.map((p) => (
              <option key={p.id} value={p.id}>{p.nickname}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-sereno-dark/50">
            <input type="checkbox" checked={incluirBajas} onChange={(e) => ponerFiltro('incluirBajas', e.target.checked ? 'true' : '')} />
            Incluir bajas
          </label>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState title="No se ha podido cargar el inventario" description={error} />
      ) : filas.length === 0 ? (
        <EmptyState
          title={hayFiltros ? 'Nada encaja con estos filtros' : 'Todavía no hay dispositivos'}
          description={hayFiltros ? 'Prueba a quitar alguno.' : 'Sincroniza con las cuentas de Nuki y SwitchBot para crear las fichas, o da de alta uno a mano.'}
          action={!hayFiltros ? (
            <button onClick={() => void sincronizar()} disabled={sincronizando} className="btn-primary disabled:opacity-50">
              {sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
            </button>
          ) : undefined}
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full hidden md:table">
            <thead>
              <tr className="border-b border-gray-100">
                <Th>Dispositivo</Th>
                <Th>Tipo</Th>
                <Th>Estado</Th>
                <Th>Dónde</Th>
                <Th>Señal</Th>
                <Th>Incidencias</Th>
                <Th>Garantía</Th>
              </tr>
            </thead>
            <tbody>
              {filas.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/dispositivos/${d.id}`)} className="border-b border-gray-50 hover:bg-sereno-blue/20 cursor-pointer transition-colors">
                  <td className="px-6 py-3">
                    <p className="font-medium text-sm font-mono">{d.idFabricante}</p>
                    <p className="text-xs text-sereno-dark/50 truncate max-w-[220px]">{d.alias || '—'}</p>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <Tipo fila={d} modeloLabel={catalogos.modeloLabel} />
                  </td>
                  <td className="px-6 py-3"><EstadoBadge estado={d.estado} /></td>
                  <td className="px-6 py-3 text-sm">
                    <Donde fila={d} />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-col gap-0.5">
                      <ChipConexion snapshot={d.snapshot} />
                      <ChipBateria snapshot={d.snapshot} />
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm"><Incidencias fila={d} /></td>
                  <td className="px-6 py-3 text-xs"><Garantia fila={d} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="md:hidden divide-y divide-gray-50">
            {filas.map((d) => (
              <div key={d.id} onClick={() => navigate(`/dispositivos/${d.id}`)} className="p-4 hover:bg-sereno-blue/20 cursor-pointer transition-colors active:bg-sereno-blue/30">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm font-mono flex-1 truncate">{d.idFabricante}</p>
                  <EstadoBadge estado={d.estado} pequeno />
                </div>
                <p className="text-xs text-sereno-dark/50 truncate">{d.alias || '—'} · <Tipo fila={d} modeloLabel={catalogos.modeloLabel} /></p>
                <p className="text-xs text-sereno-dark/60 mt-1"><Donde fila={d} /></p>
                <div className="flex items-center gap-3 mt-1">
                  <ChipConexion snapshot={d.snapshot} />
                  <ChipBateria snapshot={d.snapshot} />
                  <span className="text-xs"><Incidencias fila={d} /></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={mostrarAlta} onClose={() => setMostrarAlta(false)} title="Nuevo dispositivo" size="xl">
        <p className="text-xs text-sereno-dark/50 mb-4">
          Para lo que la sincronización no ve: Ring, bridges, cajetines, mandos… Lo que ya está en las cuentas de Nuki o SwitchBot se crea solo con «Sincronizar».
        </p>
        <FormularioDispositivo onGuardar={crear} onCancelar={() => setMostrarAlta(false)} />
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">{children}</th>;
}

function Tipo({ fila, modeloLabel }: { fila: FilaDispositivo; modeloLabel: Record<string, string> }) {
  return (
    <>
      <span>{TIPO_LABEL[fila.tipo]}</span>
      {fila.modelo ? (
        <span className="text-sereno-dark/40"> · {modeloLabel[fila.modelo] || fila.modelo}</span>
      ) : (
        <span className="text-amber-700 text-xs"> · sin modelo</span>
      )}
    </>
  );
}

function Donde({ fila }: { fila: FilaDispositivo }) {
  const principal = fila.instalaciones.find((i) => i.principal);
  const pendiente = fila.instalaciones.some((i) => i.hostifyPendiente);
  return (
    <>
      <span>{textoDonde(fila)}</span>
      {fila.estado === 'instalado' && principal && <span className="text-sereno-dark/40 text-xs"> · desde {fechaCorta(principal.desde)}</span>}
      {pendiente && (
        <span className="inline-flex items-center gap-1 ml-1 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Instalado sin escribir el custom field en Hostify">
          <AlertTriangle size={10} /> Hostify
        </span>
      )}
    </>
  );
}

function Incidencias({ fila }: { fila: FilaDispositivo }) {
  const { incidencias, incidencias90d } = fila.resumen;
  if (incidencias === 0) return <span className="text-sereno-dark/30">—</span>;
  return (
    <span className={incidencias90d > 0 ? 'text-red-700 font-medium' : 'text-sereno-dark/60'}>
      {incidencias}
      {incidencias90d > 0 && <span className="text-xs font-normal"> · {incidencias90d} en 90 d</span>}
    </span>
  );
}

function Garantia({ fila }: { fila: FilaDispositivo }) {
  const g = estadoGarantia(fila.compra);
  return <span className={g.vigente === null ? 'text-sereno-dark/30' : g.vigente ? 'text-green-700' : 'text-sereno-dark/50'}>{g.texto}</span>;
}
